import { useEffect, useState, useCallback, useMemo } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, Mail, Printer } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import AdminSearch, { matchesQuery } from '../../components/admin/AdminSearch';

function money(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

// The short order code staff read off the packing bench.
function code(id) {
  return id.slice(0, 8).toUpperCase();
}

// What staff read is not always what the database stores. `expired` means the
// customer started checkout and never paid: no card was entered and nothing
// was held. "Expired" read like a payment was taken and lost, so it says what
// actually happened. The stored value stays `expired`.
const PAY_LABEL = { expired: 'Never paid' };
const payLabel = (status) => PAY_LABEL[status] || status;

const FILTERS = [
  ['all', 'All'],
  ['needs_fulfillment', 'Needs fulfillment'],
  ['pending', 'Pending'],
  ['authorized', 'Authorized'],
  ['paid', 'Paid'],
  ['refunded', 'Refunded'],
  ['canceled', 'Canceled'],
  ['expired', 'Never paid'],
  ['failed', 'Failed'],
];

// "3 hours ago", "2 days ago" — how cold a never-paid lead is at a glance.
function ago(iso) {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `${hrs} hr ago`;
  return `${Math.round(hrs / 24)} days ago`;
}

// A pre-written nudge for someone who never finished paying. Opens the
// person's own mail app so it goes out from a real address and reads like a
// person wrote it.
function nudgeHref(o) {
  const first = (o.guest_name || o.shipping_address?.name || '').trim().split(/\s+/)[0];
  const items = (o.order_items || []).map((it) => it.product_name_snapshot).join(', ');
  const body = `Hi${first ? ` ${first}` : ''},\n\nI noticed you started an order at Shiny Vault LGS (${items}) but it looks like it didn't get finished. Did anything get in the way? I'm happy to help get it wrapped up.\n\nThanks so much,\nShiny Vault LGS`;
  return `mailto:${o.guest_email}?subject=${encodeURIComponent('Your Shiny Vault order')}&body=${encodeURIComponent(body)}`;
}

const FULFILLMENT_STEPS_SHIP = ['unfulfilled', 'label_purchased', 'shipped', 'completed'];
const FULFILLMENT_STEPS_PICKUP = ['unfulfilled', 'ready_for_pickup', 'picked_up', 'completed'];

export default function AdminOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | pending | paid | needs_fulfillment
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  // Separate from `error` on purpose. These are things that went wrong AFTER an
  // action already succeeded — a refund that paid out but did not restock, an
  // email that did not send. Colouring them as failures would suggest the
  // action needs redoing, and hiding them is what let a dead restock sit
  // unnoticed for three days (2026-08-29).
  const [warnings, setWarnings] = useState([]);

  const load = useCallback(async () => {
    const { data } = await supabase
      .from('orders')
      .select('*, order_items(*)')
      .order('created_at', { ascending: false });
    setOrders(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const byStatus = useMemo(() => {
    // Never-paid checkouts are leads, not orders. They live on their own tab so
    // the default list is only real sales.
    if (filter === 'all') return orders.filter((o) => o.payment_status !== 'expired');
    if (filter === 'needs_fulfillment') {
      return orders.filter((o) => ['paid', 'authorized'].includes(o.payment_status)
        && !['completed', 'picked_up', 'shipped'].includes(o.fulfillment_status));
    }
    return orders.filter((o) => o.payment_status === filter);
  }, [orders, filter]);

  // Searchable by anything staff would have in front of them: the short order
  // code they read off a card, customer name or email, a tracking number from
  // a carrier email, the destination, or the name of a product in the order.
  const filtered = useMemo(() => {
    if (!query.trim()) return byStatus;
    return byStatus.filter((o) => matchesQuery(query, [
      o.id,
      o.id?.slice(0, 8),
      o.guest_name, o.guest_email,
      o.tracking_number, o.shipping_carrier, o.shipping_service,
      o.fulfillment_method, o.fulfillment_status, o.payment_status,
      o.shipping_address?.name, o.shipping_address?.city,
      o.shipping_address?.state, o.shipping_address?.zip,
      (o.order_items || []).map((it) => it.product_name_snapshot).join(' '),
      o.total_cents != null ? (o.total_cents / 100).toFixed(2) : '',
    ]));
  }, [byStatus, query]);

  const neverPaidCount = useMemo(
    () => orders.filter((o) => o.payment_status === 'expired').length,
    [orders],
  );

  const advanceFulfillment = async (order) => {
    const steps = order.fulfillment_method === 'ship' ? FULFILLMENT_STEPS_SHIP : FULFILLMENT_STEPS_PICKUP;
    const idx = steps.indexOf(order.fulfillment_status);
    const next = steps[idx + 1];
    if (!next) return;
    setBusyId(order.id);
    await supabase.from('orders').update({ fulfillment_status: next }).eq('id', order.id);
    // The confirmation email already carried tracking for the normal path
    // (webhook buys the label the moment payment clears). This covers the case
    // where the label was bought late — a retry after a failure — so the
    // customer never got the number. Fire-and-forget: a mail failure shouldn't
    // block the status change staff just made.
    if (next === 'shipped' && order.tracking_number) {
      supabase.functions
        .invoke('shinyvault-order-email', { body: { type: 'order_shipped', order_id: order.id } })
        .then(({ data, error: mailErr }) => {
          const reason = data?.error?.message || data?.error || mailErr?.message;
          if (reason || data?.ok === false) {
            setWarnings((w) => [...w, `Order #${code(order.id)} is marked shipped, but the "shipped" email did not send${reason ? ` (${reason})` : ''}. Tell the customer their tracking number another way.`]);
          }
        })
        .catch((e) => {
          setWarnings((w) => [...w, `Order #${code(order.id)} is marked shipped, but the "shipped" email did not send (${e.message}).`]);
        });
    }
    await load();
    setBusyId(null);
  };

  const refund = async (order) => {
    const held = order.payment_status === 'authorized';
    if (!window.confirm(held
      ? `Release the ${money(order.total_cents)} hold on order #${code(order.id)}?\n\n`
        + 'The customer was never charged, so nothing is refunded — the hold is '
        + 'cancelled and the money is freed immediately.'
      : `Refund ${money(order.total_cents)} for order #${code(order.id)}?`)) return;
    setBusyId(order.id);
    setError('');
    setWarnings([]);
    const { data, error: err } = await supabase.functions.invoke('shinyvault-checkout', {
      body: { action: 'admin_refund', order_id: order.id },
    });
    if (err || data?.error) setError(data?.error || err.message || 'Refund failed');
    // The money moved but a cleanup step did not. Staff have to see this: the
    // usual case is an item that never went back on the shelf.
    else if (data?.warnings?.length) {
      setWarnings(data.warnings.map((w) => `Order #${code(order.id)} was ${held ? 'released' : 'refunded'}, but ${w}`));
    }
    await load();
    setBusyId(null);
  };

  if (loading) return <div>Loading…</div>;

  return (
    <div>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 900, marginBottom: 20 }}>Orders</h1>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {FILTERS.map(([f, label]) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={filter === f ? 'sv-btn' : 'sv-btn-outline'}
            style={{ padding: '7px 14px', fontSize: '0.82rem' }}
          >
            {label}{f === 'expired' && neverPaidCount > 0 ? ` (${neverPaidCount})` : ''}
          </button>
        ))}
      </div>

      <AdminSearch
        value={query}
        onChange={setQuery}
        placeholder="Search order #, customer, tracking, product…"
        resultCount={filtered.length}
        totalCount={byStatus.length}
      />

      {error && <div style={{ color: 'var(--danger)', marginBottom: 16, fontSize: '0.85rem' }}>{error}</div>}

      {warnings.length > 0 && (
        <div
          style={{
            border: '1px solid var(--warning)',
            borderRadius: 10,
            padding: '12px 14px',
            marginBottom: 16,
            fontSize: '0.85rem',
            lineHeight: 1.5,
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
          }}
        >
          <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2, color: 'var(--warning)' }} />
          <div style={{ flex: 1 }}>
            {warnings.map((w, i) => <div key={i} style={{ marginBottom: i === warnings.length - 1 ? 0 : 6 }}>{w}</div>)}
          </div>
          <button
            onClick={() => setWarnings([])}
            className="sv-btn-outline"
            style={{ padding: '4px 10px', fontSize: '0.75rem', flexShrink: 0 }}
          >
            Dismiss
          </button>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {filtered.map((o) => {
          const isOpen = expanded === o.id;
          const steps = o.fulfillment_method === 'ship' ? FULFILLMENT_STEPS_SHIP : FULFILLMENT_STEPS_PICKUP;
          const stepIdx = steps.indexOf(o.fulfillment_status);
          const hasNext = o.payment_status === 'paid' && stepIdx >= 0 && stepIdx < steps.length - 1;

          return (
            <div key={o.id} className="sv-card" style={{ overflow: 'hidden' }}>
              <div
                onClick={() => setExpanded(isOpen ? null : o.id)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', cursor: 'pointer' }}
              >
                <div>
                  <div style={{ fontWeight: 700 }}>
                    #{o.id.slice(0, 8).toUpperCase()} — {o.guest_name || o.guest_email || 'Account holder'}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    {o.payment_status === 'expired'
                      ? <>{new Date(o.created_at).toLocaleString()} ({ago(o.created_at)}) · {payLabel(o.payment_status)}{o.guest_email ? ` · ${o.guest_email}` : ''}</>
                      : <>{new Date(o.created_at).toLocaleString()} · {payLabel(o.payment_status)} · {o.fulfillment_method} · {o.fulfillment_status}</>}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ fontWeight: 800 }}>{money(o.total_cents)}</div>
                  {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
              </div>

              {isOpen && (
                <div style={{ padding: '0 16px 16px', borderTop: '1px solid var(--border)' }}>
                  <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6, fontSize: '0.85rem' }}>
                    {(o.order_items || []).map((it) => (
                      <div key={it.id} style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>{it.quantity}× {it.product_name_snapshot}</span>
                        <span>{money(it.price_cents_snapshot * it.quantity)}</span>
                      </div>
                    ))}
                  </div>

                  {o.fulfillment_method === 'ship' && o.shipping_address && (
                    <div style={{ marginTop: 12, fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                      {o.shipping_address.name ? <>{o.shipping_address.name}<br /></> : null}
                      {o.shipping_address.street1}{o.shipping_address.street2 ? `, ${o.shipping_address.street2}` : ''}, {o.shipping_address.city}, {o.shipping_address.state} {o.shipping_address.zip}
                      {o.tracking_number && (
                        <>
                          {' · Tracking: '}
                          {o.tracking_url
                            ? <a href={o.tracking_url} target="_blank" rel="noopener noreferrer">{o.tracking_number}</a>
                            : o.tracking_number}
                        </>
                      )}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
                    {o.payment_status === 'expired' && o.guest_email && (
                      <a
                        href={nudgeHref(o)}
                        className="sv-btn"
                        style={{ padding: '7px 14px', fontSize: '0.82rem', display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}
                      >
                        <Mail size={14} />
                        Email them
                      </a>
                    )}
                    {o.label_url && (
                      <a
                        href={o.label_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="sv-btn"
                        style={{ padding: '7px 14px', fontSize: '0.82rem', display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}
                      >
                        <Printer size={14} />
                        Print label
                      </a>
                    )}
                    {hasNext && (
                      <button
                        onClick={() => advanceFulfillment(o)}
                        disabled={busyId === o.id}
                        className="sv-btn"
                        style={{ padding: '7px 14px', fontSize: '0.82rem' }}
                      >
                        Mark {steps[stepIdx + 1].replace(/_/g, ' ')}
                      </button>
                    )}
                    {['paid', 'authorized'].includes(o.payment_status) && (
                      <button
                        onClick={() => refund(o)}
                        disabled={busyId === o.id}
                        className="sv-btn-outline"
                        style={{ padding: '7px 14px', fontSize: '0.82rem' }}
                        title={o.payment_status === 'authorized'
                          ? 'Cancel the hold. Nothing was charged, so nothing is refunded.'
                          : 'Refund the customer'}
                      >
                        {o.payment_status === 'authorized' ? 'Release hold' : 'Refund'}
                      </button>
                    )}
                    {o.receipt_url && (
                      <a href={o.receipt_url} target="_blank" rel="noreferrer" className="sv-btn-outline" style={{ padding: '7px 14px', fontSize: '0.82rem' }}>
                        Receipt
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>
            {filter === 'expired' ? 'Nobody has walked away from a checkout.' : 'No orders.'}
          </div>
        )}
      </div>
    </div>
  );
}
